import { EDesignerEntityKind } from "@kuika/kuika-cl-model";
import { EntityModificationService } from "../../services/entity-modification-service";
import { buildModificationIndex, type FeedStatus, type ModificationIndex } from "./recently-modified";

/**
 * The fetched state of one landing's "Recently modified" feed. Kept as one object so a landing
 * holds a single piece of state and cannot end up with a ready status over a stale index.
 */
export interface IModificationFeed {
  status: FeedStatus;
  index: ModificationIndex;
}

/** What a landing starts with: nothing known yet, nothing to show a date from. */
export const LOADING_FEED: IModificationFeed = {
  status: "loading",
  index: new Map()
};

/**
 * Fetches the feed for the kinds a landing lists and indexes it by entity id.
 *
 * Never throws and never rejects: a failed call answers a failed feed, which resolves the tab to
 * the full list and renders the column as em dashes. The landing's own list is unaffected.
 */
export const loadModificationFeed = async (
  kinds: EDesignerEntityKind[],
  projectId?: string
): Promise<IModificationFeed> => {
  const entries = await EntityModificationService.GetModifications(kinds, projectId);
  if (entries === null) {
    return { status: "failed", index: new Map() };
  }

  return { status: "ready", index: buildModificationIndex(entries) };
};

/**
 * A signature of an item list's live ids, order-independent. Two calls compare equal exactly
 * when the same set of ids is live: a create, a duplicate, or a delete changes it. An in-place
 * edit of an existing item (a rename, a saved change) does not, because a row's own fields — never
 * this signature — are what a landing renders for it, and the feed's own reply cannot see a rename
 * either.
 */
export const itemIdSignature = <T>(items: readonly T[], getId: (item: T) => string | undefined): string => {
  const ids: string[] = [];
  for (const item of items) {
    const id: string | undefined = getId(item);
    if (id) ids.push(id);
  }
  ids.sort();
  return ids.join("\u0000");
};

/**
 * Whether a landing's live item set changed between two renders — the signal that its
 * "Recently modified" feed, fetched once on mount, is due for a reload. Id-set equality is the
 * smallest correct signal: it catches a create, a duplicate, and a delete without polling.
 */
export const haveItemIdsChanged = <T>(
  previousItems: readonly T[],
  nextItems: readonly T[],
  getId: (item: T) => string | undefined
): boolean => itemIdSignature(previousItems, getId) !== itemIdSignature(nextItems, getId);

/**
 * Guards a landing's feed reloads against out-of-order replies: if a second reload starts before
 * the first settles, the second reload's answer must win even when the first's network call
 * happens to finish later. Each landing keeps ONE guard instance of its own — sharing one across
 * two mounted landings (several live under `data-diagram/` at once) would cross-talk between them.
 */
export interface IFeedRequestGuard {
  /** Call once per reload attempt, before the fetch starts; hold the token to check it later. */
  start: () => number;
  /** True when `token` is still the most recent one `start` produced. */
  isCurrent: (token: number) => boolean;
}

export const createFeedRequestGuard = (): IFeedRequestGuard => {
  let latestToken: number = 0;
  return {
    start: (): number => {
      latestToken += 1;
      return latestToken;
    },
    isCurrent: (token: number): boolean => token === latestToken
  };
};
